import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Search, X, ArrowUpDown, Hourglass, BadgeCheck, XCircle, Inbox, MapPin, AlertTriangle, RefreshCw, AlertCircle } from "lucide-react";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Sidebar1 from "components/Sidebar1";
import { FONT_HEADING, PAGE_SIZES, Pagination, EmptyState, CardSkeleton } from "../userManagement/shared";
import ApplicationCard from "./ApplicationCard";
import DocumentModal from "./DocumentModal";
import { handleAuthError } from "../userManagement/shared";
import {
  EMIRATES, useApplicationsState, loadApplications, approveApplication, rejectApplication, isLicenseExpired,
} from "./applicationsStore";

const PAGES = {
  pending: {
    title: "Pending Applications",
    subtitle: "New service providers waiting for verification. Review their documents, then approve or reject.",
    Icon: Hourglass,
    empty: "No applications waiting — you’re all caught up.",
  },
  approved: {
    title: "Approved Applications",
    subtitle: "Verified service providers who can create shops and take bookings in the app.",
    Icon: BadgeCheck,
    empty: "No approved applications yet.",
  },
  rejected: {
    title: "Rejected Applications",
    subtitle: "Applications that didn’t pass verification, with the reason sent to the provider.",
    Icon: XCircle,
    empty: "No rejected applications.",
  },
};

const TABS = [
  { status: "pending", label: "Pending", path: "/applications/pending", Icon: Hourglass, color: "#F59E0B" },
  { status: "approved", label: "Approved", path: "/applications/approved", Icon: BadgeCheck, color: "#16A34A" },
  { status: "rejected", label: "Rejected", path: "/applications/rejected", Icon: XCircle, color: "#EA352B" },
];

const SORTS = {
  newest: { label: "Newest first", fn: (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt) },
  oldest: { label: "Oldest first", fn: (a, b) => new Date(a.submittedAt) - new Date(b.submittedAt) },
  name: { label: "Business A–Z", fn: (a, b) => a.title.localeCompare(b.title) },
};

export default function ApplicationsPage({ status }) {
  const page = PAGES[status];
  const { items: apps, loading, loaded, error } = useApplicationsState();

  const [searchTerm, setSearchTerm] = useState("");
  const [emirate, setEmirate] = useState("all");
  const [sortKey, setSortKey] = useState(status === "pending" ? "oldest" : "newest");
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [modalMode, setModalMode] = useState("view");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!loaded) loadApplications();
  }, [loaded]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, emirate, sortKey, pageSize, status]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    apps.forEach((a) => { c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [apps]);

  const inStatus = useMemo(() => apps.filter((a) => a.status === status), [apps, status]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return inStatus
      .filter((a) => emirate === "all" || a.emirate === emirate)
      .filter((a) =>
        !term ||
        [a.title, a.owner, a.email, a.phone, a.vat, a.license, a.location, ...(a.services || []), ...(a.shops || []).map((s) => s.name)].some((v) => String(v || "").toLowerCase().includes(term))
      )
      .sort(SORTS[sortKey].fn);
  }, [inStatus, searchTerm, emirate, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const currentItems = filtered.slice(pageStart, pageStart + pageSize);

  const selected = apps.find((a) => a.id === selectedId) || null;
  const open = (a, mode = "view") => { setModalMode(mode); setSelectedId(a.id); };
  const close = () => setSelectedId(null);

  // The API client already toasts request errors
  const review = async (id, action, successToast) => {
    if (busyId) return;
    setBusyId(id);
    try {
      await action();
      successToast();
      close();
    } catch (error) {
      console.error("Review application error:", error.response?.data || error);
      handleAuthError(error);
    } finally {
      setBusyId(null);
    }
  };
  const approve = (id) => {
    const a = apps.find((x) => x.id === id);
    review(id, () => approveApplication(id), () => toast.success(`${a?.title || "Application"} approved — moved to Approved.`));
  };
  const reject = (id, reason) => {
    const a = apps.find((x) => x.id === id);
    review(id, () => rejectApplication(id, reason), () =>
      toast.info(`${a?.title || "Application"} ${a?.status === "approved" ? "approval revoked" : "rejected"} — moved to Rejected.`));
  };

  // Quick insights for the current list
  const insights = useMemo(() => {
    const byEmirate = {};
    inStatus.forEach((a) => { byEmirate[a.emirate] = (byEmirate[a.emirate] || 0) + 1; });
    const topEmirate = Object.entries(byEmirate).sort((a, b) => b[1] - a[1])[0];
    const oldest = [...inStatus].sort(SORTS.oldest.fn)[0];
    return {
      thisWeek: inStatus.filter((a) => moment(status === "pending" ? a.submittedAt : a.reviewedAt || a.submittedAt).isAfter(moment().subtract(7, "days"))).length,
      expired: inStatus.filter(isLicenseExpired).length,
      topEmirate,
      oldestWait: oldest ? moment(oldest.submittedAt).fromNow(true) : null,
      oldestOverdue: oldest ? moment(oldest.submittedAt).isBefore(moment().subtract(2, "days")) : false,
    };
  }, [inStatus, status]);

  const emiratesInList = EMIRATES.filter((e) => inStatus.some((a) => a.emirate === e));

  return (
    <div className="flex h-screen bg-[#F6F6F8] overflow-hidden">
      <Helmet><title>Fuelingo | {page.title}</title></Helmet>
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar />
      <Sidebar1 />

      <main className="flex-1 min-w-0 overflow-y-auto px-8 py-6 lg:px-6 md:px-4 sm:px-3">
        {/* Header */}
        <header className="flex items-start justify-between gap-4 mb-6 sm:flex-col">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="m-0 text-3xl sm:text-2xl font-medium text-[#111]" style={FONT_HEADING}>{page.title}</h1>
            </div>
            <p className="m-0 mt-1 text-sm text-[#6B7280] max-w-[640px]">{page.subtitle}</p>
          </div>
          <button
            type="button"
            onClick={loadApplications}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-[#fff] border border-[#ECECEF] px-4 py-2.5 text-sm font-medium text-[#374151] hover:border-[#EA352B] hover:text-[#EA352B] transition disabled:opacity-60"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </header>

        {/* Status tabs (routes) */}
        <nav className="grid grid-cols-3 gap-3 mb-6" aria-label="Application status">
          {TABS.map((t) => {
            const active = t.status === status;
            return (
              <NavLink
                key={t.status}
                to={t.path}
                className={`no-underline rounded-2xl border p-4 sm:p-3 transition-all ${
                  active ? "bg-[#1B1B22] border-[#1B1B22] shadow-[0_10px_30px_rgba(27,27,34,0.25)]" : "bg-[#fff] border-[#EEEEF1] hover:border-[#D9DAE0]"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="w-10 h-10 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center" style={{ background: active ? t.color : `${t.color}1f` }}>
                    <t.Icon size={18} style={{ color: active ? "#fff" : t.color }} />
                  </span>
                  <span className={`text-2xl sm:text-xl font-semibold ${active ? "text-[#fff]" : "text-[#111]"}`}>{counts[t.status]}</span>
                </div>
                <p className={`m-0 mt-3 text-sm font-semibold ${active ? "text-[#fff]" : "text-[#111]"}`}>{t.label}</p>
                {t.status === "pending" && counts.pending > 0 && (
                  <p className={`m-0 text-[11px] sm:hidden ${active ? "text-[#FCA5A5]" : "text-[#B45309]"}`}>
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#F59E0B] mr-1.5 align-middle animate-pulse" />
                    Needs review
                  </p>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Insights strip */}
        <section className="grid grid-cols-3 sm:grid-cols-1 gap-3 mb-6">
          <Insight label={status === "pending" ? "Submitted this week" : status === "approved" ? "Approved this week" : "Rejected this week"} value={insights.thisWeek} />
          {status === "pending" ? (
            <Insight label="Longest waiting" value={insights.oldestWait || "—"} warn={insights.oldestOverdue} />
          ) : (
            <Insight label="Top emirate" value={insights.topEmirate ? `${insights.topEmirate[0]} · ${insights.topEmirate[1]}` : "—"} />
          )}
          <Insight label="Expired trade licenses" value={insights.expired} warn={insights.expired > 0} />
        </section>

        {/* List */}
        <section className="bg-[#fff] rounded-3xl border border-[#EEEEF1] p-6 md:p-5 sm:p-4" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
          <div className="flex items-center gap-3 flex-wrap mb-5">
            <div className="relative flex-1 min-w-[220px] sm:min-w-0 sm:w-full">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
              {/* No `type` attr: styles/tailwind.css resets padding/border on typed inputs */}
              <input
                role="searchbox"
                enterKeyHint="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
                placeholder="Search business, owner, TRN, license or service"
                className="w-full pl-10 pr-9 py-2.5 bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl text-sm text-[#111] placeholder-[#9CA3AF] focus:outline-none focus:border-[#EA352B] focus:bg-[#fff] focus:ring-0 transition"
              />
              {searchTerm && (
                <button type="button" aria-label="Clear search" onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111]">
                  <X size={15} />
                </button>
              )}
            </div>
            <label className="relative inline-flex items-center sm:flex-1">
              <MapPin size={14} className="absolute left-3 text-[#9CA3AF] pointer-events-none" />
              <select
                value={emirate}
                onChange={(e) => setEmirate(e.target.value)}
                aria-label="Filter by emirate"
                className="w-full appearance-none bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl pl-8 pr-8 py-2.5 text-sm text-[#374151] focus:outline-none focus:border-[#EA352B] focus:ring-0 cursor-pointer"
              >
                <option value="all">All emirates</option>
                {emiratesInList.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </label>
            <label className="relative inline-flex items-center sm:flex-1">
              <ArrowUpDown size={14} className="absolute left-3 text-[#9CA3AF] pointer-events-none" />
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                aria-label="Sort applications"
                className="w-full appearance-none bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl pl-8 pr-8 py-2.5 text-sm text-[#374151] focus:outline-none focus:border-[#EA352B] focus:ring-0 cursor-pointer"
              >
                {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
              </select>
            </label>
          </div>

          {loading && !apps.length ? (
            <div className="grid grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
            </div>
          ) : error && !apps.length ? (
            <EmptyState
              Icon={AlertCircle}
              title={error}
              text="Check your connection and try again."
              action={<button type="button" onClick={loadApplications} className="mt-3 text-sm font-semibold text-[#EA352B]">Try again</button>}
            />
          ) : currentItems.length === 0 ? (
            <EmptyState
              Icon={inStatus.length ? Search : Inbox}
              title={inStatus.length ? "No applications match" : page.empty}
              text={inStatus.length ? "Try a different search or emirate." : ""}
              action={inStatus.length ? (
                <button type="button" onClick={() => { setSearchTerm(""); setEmirate("all"); }} className="mt-3 text-sm font-semibold text-[#EA352B]">Clear filters</button>
              ) : null}
            />
          ) : (
            <div className="grid grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 gap-4">
              {currentItems.map((a) => (
                <ApplicationCard
                  key={a.id}
                  data={a}
                  busy={busyId === a.id}
                  onView={() => open(a)}
                  onApprove={() => approve(a.id)}
                  onReject={() => open(a, "reject")}
                />
              ))}
            </div>
          )}

          {filtered.length > 0 && (
            <div className="flex items-center justify-between gap-3 flex-wrap mt-6 pt-4 border-t border-[#F1F1F4]">
              <div className="flex items-center gap-3 text-xs text-[#6B7280]">
                <span>
                  Showing <b className="text-[#111]">{pageStart + 1}–{Math.min(pageStart + pageSize, filtered.length)}</b> of <b className="text-[#111]">{filtered.length}</b>
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  aria-label="Items per page"
                  className="bg-[#F7F7F9] border border-[#EEEEF1] rounded-lg py-1 pl-2 pr-7 text-xs focus:outline-none focus:ring-0 focus:border-[#EA352B]"
                >
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>
              </div>
              {totalPages > 1 && <Pagination page={currentPage} total={totalPages} onChange={setCurrentPage} />}
            </div>
          )}
        </section>
      </main>

      <DocumentModal data={selected} mode={modalMode} busy={!!selected && busyId === selected.id} onClose={close} onApprove={approve} onReject={reject} />
    </div>
  );
}

function Insight({ label, value, warn }) {
  return (
    <div className={`rounded-2xl border px-4 py-3 flex items-center justify-between gap-3 ${warn ? "bg-[#FFFBEB] border-[#FDE68A]" : "bg-[#fff] border-[#EEEEF1]"}`}>
      <span className={`text-xs font-medium inline-flex items-center gap-1.5 ${warn ? "text-[#92400E]" : "text-[#6B7280]"}`}>
        {warn && <AlertTriangle size={13} />} {label}
      </span>
      <span className={`text-lg font-semibold ${warn ? "text-[#92400E]" : "text-[#111]"}`}>{value}</span>
    </div>
  );
}
